import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lightning, ChartLineUp, Brain, ShieldCheck } from "@phosphor-icons/react";
import { Header, Disclaimer } from "../components/Header";

const SPORT_IMAGES = {
  NBA: "https://images.unsplash.com/photo-1608245449230-4ac19066d2d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  NFL: "https://images.unsplash.com/photo-1544765773-a8dce1f272f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  MLB: "https://images.unsplash.com/photo-1529768167801-9173d94c2a42?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  NHL: "https://images.unsplash.com/flagged/photo-1550585477-a025700d7fce?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
};

const FEATURES = [
  { icon: ChartLineUp, title: "Real probability math", text: "We strip the vig from market odds to find true win probability and expected value on every leg." },
  { icon: Brain, title: "AI coach's notes", text: "Every parlay comes with a plain-English breakdown of why the legs fit together — no jargon." },
  { icon: ShieldCheck, title: "Risk you control", text: "Safe, Medium, or Lotto. Pick your risk lane and leg count — we build three optimized tickets." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-slate-50 noise-bg">
      <Header />
      <main className="relative z-10">
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#00E676] mb-4">Sports analytics · For entertainment</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.95] max-w-3xl">
              Build smarter parlays.<br />
              <span className="text-[#00E676]">Not luckier ones.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-xl leading-relaxed">
              Pick a sport, set your risk, choose your legs. Our probability engine finds the best-value
              combinations and an AI coach explains every ticket in plain English.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/auth" data-testid="hero-cta-btn"
                className="rounded-full bg-[#00E676] text-black font-bold px-8 py-3.5 text-base hover:scale-105 active:scale-95 transition-transform inline-flex items-center gap-2">
                <Lightning size={20} weight="fill" /> Build your first parlay free
              </Link>
              <Link to="/pricing" data-testid="hero-pricing-link"
                className="text-sm font-bold text-slate-300 hover:text-white transition-colors underline underline-offset-4">
                See pricing
              </Link>
              <span className="text-xs text-slate-500 font-medium">21+ only · No wagers placed</span>
            </div>
          </motion.div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(SPORT_IMAGES).map(([name, img], i) => (
              <motion.div key={name} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.1 }}
                className="relative h-40 sm:h-52 rounded-xl overflow-hidden border border-white/10 group">
                <img src={img} alt={name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <span className="absolute bottom-3 left-4 font-display text-3xl font-black uppercase tracking-tight">{name}</span>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
          <div className="grid sm:grid-cols-3 gap-6">
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-[#141419] border border-white/10 rounded-xl p-6">
                <Icon size={28} weight="bold" className="text-[#2563EB] mb-4" />
                <h3 className="font-display text-xl font-bold uppercase tracking-tight mb-2">{title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Disclaimer />
    </div>
  );
}
